import useCalendar from '@/hooks/calendar/useCalendar';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages from '@/hooks/native/useNativeMessages';
import useSpecies from '@/hooks/species/useSpecies';
import { capitalizeFirstLetter } from '@/lib/string';
import { useCallback, useEffect, useState } from 'react';

const useNotifications = () => {
	const isNative = useIsNative();
	const { sendNativeMessage, awaitSingleNativeMessage } = useNativeMessages();
	const { calendar } = useCalendar();
	const { findSpeciesById } = useSpecies();

	const [
		isNotificationPermissionGranted,
		setIsNotificationPermissionGranted,
	] = useState<boolean | null>(null);
	const [scheduledNotifications, setScheduledNotifications] = useState<
		{
			id: string;
			title: string;
			description: string;
		}[]
	>([]);

	// Check notification permission
	const checkNotificationPermission = useCallback(() => {
		if (isNative) {
			sendNativeMessage('notifications.requestPermission');
			awaitSingleNativeMessage('notifications.requestPermission').then(
				(value) => setIsNotificationPermissionGranted(value === 'true'),
			);
		}
	}, [awaitSingleNativeMessage, isNative, sendNativeMessage]);

	// Check notification permission on mount
	// Check a few times after app start too, since initial check might fail
	useEffect(() => {
		let checksLeft = 6;
		const interval = setInterval(() => {
			if (checksLeft === 0) clearInterval(interval);
			checksLeft -= 1;
			checkNotificationPermission();
		}, 2500);
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [checkNotificationPermission]);

	// Request notification permission
	const requestNotificationPermission = useCallback(() => {
		if (isNative) {
			sendNativeMessage('notifications.requestPermission');
			awaitSingleNativeMessage('notifications.requestPermission').then(
				(value) => {
					setIsNotificationPermissionGranted(value === 'true');
					if (value === 'false')
						sendNativeMessage(
							'alert.show',
							'Het is niet gelukt om notificaties in te stellen. Probeer het via je instellingen app toe te staan.',
						);
				},
			);
		}
	}, [awaitSingleNativeMessage, isNative, sendNativeMessage]);

	// Get list of scheduled notifications
	const getScheduledNotifications = useCallback(() => {
		if (isNative) {
			sendNativeMessage('notifications.list');
			awaitSingleNativeMessage('notifications.list').then((value) => {
				setScheduledNotifications(JSON.parse(value));
			});
		}
	}, [awaitSingleNativeMessage, isNative, sendNativeMessage]);

	// Update scheduled notifications on mount and when permissions changes
	useEffect(() => {
		getScheduledNotifications();
	}, [getScheduledNotifications, isNotificationPermissionGranted]);

	// Schedule a notification
	const scheduleNotification = useCallback(
		({
			id,
			date,
			title,
			description,
		}: {
			id: string;
			date: Date;
			title: string;
			description: string;
		}) => {
			if (isNative) {
				sendNativeMessage(
					'notifications.schedule',
					// Combine date and id to create a unique identifier
					JSON.stringify({
						id: `${date.toISOString()}@${id}`,
						date: date.toISOString(),
						title,
						description,
					}),
				);
				getScheduledNotifications();
			}
		},
		[getScheduledNotifications, isNative, sendNativeMessage],
	);

	// Schedule a notification for each species in the calendar that is in the future
	useEffect(() => {
		if (calendar && isNotificationPermissionGranted) {
			calendar.data.forEach((entry) => {
				const speciesId = entry.attributes?.speciesEntry?.data?.id;
				const species = findSpeciesById(
					entry.attributes.speciesEntry.data?.id,
				);
				if (species) {
					// Get date and set the time to 15:30
					const date = new Date(entry.attributes.date);
					date.setHours(15, 30, 0, 0);
					// If the date is in the future, schedule a notification
					if (date > new Date()) {
						scheduleNotification({
							id: `species@${speciesId}@${species.attributes.slug}`,
							date,
							title: `${capitalizeFirstLetter(species.attributes.titleLong)} - Soort van deze week`,
							description: `Hoi daar, Janouk hier! 👋 Scoor deze week een ${species.attributes.titleLong} en verzamel extra veel appels!! 📸🍏`,
						});
					}
				}
			});
		}
	}, [
		calendar,
		findSpeciesById,
		isNotificationPermissionGranted,
		scheduleNotification,
	]);

	return {
		isNotificationPermissionGranted,
		checkNotificationPermission,
		requestNotificationPermission,
		scheduledNotifications,
		scheduleNotification,
	};
};

export default useNotifications;
